import React, {useMemo, useRef, useState} from "react";

import "./index.scss";

// ── Outcome types ─────────────────────────────────────────────────────────────

const OUTCOMES = [
	{value: "save", label: "Parata", color: "#22c55e", icon: "✓"},
	{value: "goal", label: "Gol subito", color: "#dc2626", icon: "✕"},
	{value: "shot", label: "Tiro (fuori/palo)", color: "#f59e0b", icon: "○"},
];

const getOutcomeConfig = (point) => {
	const outcome = (point.outcome || point.type || point.result || "save").toLowerCase();
	if (outcome.includes("goal") || outcome.includes("scored")) return OUTCOMES[1];
	if (outcome.includes("shot") || outcome.includes("miss") || outcome.includes("post")) return OUTCOMES[2];
	return OUTCOMES[0];
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizePoints = (points) => {
	if (Array.isArray(points)) return points;
	if (typeof points === "string") {
		try {
			const parsed = JSON.parse(points);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
};

// ── Component ─────────────────────────────────────────────────────────────────

const ShotTargetMap = ({points, onChange, readOnly = false, className = ""}) => {
	const goalRef = useRef(null);
	const fieldRef = useRef(null);
	const normalizedPoints = useMemo(() => normalizePoints(points), [points]);
	const [selectedOutcome, setSelectedOutcome] = useState("save");
	const [hoverPoint, setHoverPoint] = useState(null);
	const [pendingOrigin, setPendingOrigin] = useState(null);

	// ── Click sul campo: segna origine del tiro ───────────────────────────────

	const handleFieldClick = (event) => {
		if (readOnly || !fieldRef.current) return;
		const rect = fieldRef.current.getBoundingClientRect();
		const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
		const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
		setPendingOrigin({x: Number(x.toFixed(2)), y: Number(y.toFixed(2))});
	};

	// ── Click sulla porta: segna arrivo del tiro ──────────────────────────────

	const handleGoalClick = (event) => {
		if (readOnly || !goalRef.current) return;
		const rect = goalRef.current.getBoundingClientRect();
		const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
		const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);

		const newPoint = {
			x: Number(x.toFixed(2)),
			y: Number(y.toFixed(2)),
			outcome: selectedOutcome,
			originX: pendingOrigin?.x ?? null,
			originY: pendingOrigin?.y ?? null,
		};

		onChange?.([...normalizedPoints, newPoint]);
		setPendingOrigin(null);
	};

	// ── Rimuovi punto ─────────────────────────────────────────────────────────

	const handleRemovePoint = (index, event) => {
		if (readOnly) return;
		event.stopPropagation();
		onChange?.(normalizedPoints.filter((_, i) => i !== index));
	};

	// ── Stats ─────────────────────────────────────────────────────────────────

	const stats = useMemo(() => {
		const pts = normalizedPoints;
		const goals = pts.filter((p) => getOutcomeConfig(p) === OUTCOMES[1]).length;
		const saves = pts.filter((p) => getOutcomeConfig(p) === OUTCOMES[0]).length;
		const shots = pts.filter((p) => getOutcomeConfig(p) === OUTCOMES[2]).length;
		const total = pts.length;
		const saveRate = total > 0 ? Math.round((saves / total) * 100) : 0;
		return {total, goals, saves, shots, saveRate};
	}, [normalizedPoints]);

	// ── SVG lines (campo → porta) ─────────────────────────────────────────────

	const renderFieldLines = () => {
		return normalizedPoints.map((point, i) => {
			if (point.originX == null || point.originY == null) return null;
			const config = getOutcomeConfig(point);
			return (
				<line
					key={i}
					x1={`${point.originX}%`}
					y1={`${point.originY}%`}
					x2={`${50}%`}
					y2="0%"
					stroke={config.color}
					strokeWidth="2"
					strokeOpacity="0.5"
					strokeDasharray="4 3"
				/>
			);
		});
	};

	const renderFieldOrigins = () => {
		return normalizedPoints.map((point, i) => {
			if (point.originX == null || point.originY == null) return null;
			const config = getOutcomeConfig(point);
			return (
				<div
					key={`origin-${i}`}
					className="stm__field-origin"
					style={{
						left: `${point.originX}%`,
						top: `${point.originY}%`,
						"--origin-color": config.color,
					}}
					onClick={(e) => handleRemovePoint(i, e)}
					onMouseEnter={() => setHoverPoint(i)}
					onMouseLeave={() => setHoverPoint(null)}
				>
					<span className="stm__field-origin-dot" />
				</div>
			);
		});
	};

	return (
		<div className={`stm ${className}`}>
			{/* ── Outcome selector ────────────────────────────── */}
			{!readOnly && (
				<div className="stm__tools">
					<span className="stm__tools-label">Tipo tiro:</span>
					{OUTCOMES.map((o) => (
						<button
							key={o.value}
							className={`stm__tool-btn${selectedOutcome === o.value ? " stm__tool-btn--active" : ""}`}
							style={{
								borderColor: selectedOutcome === o.value ? o.color : "#e2e8f0",
								background: selectedOutcome === o.value ? o.color : "#fff",
								color: selectedOutcome === o.value ? "#fff" : o.color,
							}}
							onClick={() => setSelectedOutcome(o.value)}
						>
							<span className="stm__tool-icon">{o.icon}</span>
							{o.label}
						</button>
					))}
				</div>
			)}

			{/* ── Pitch + Goal combined view ──────────────────── */}
			<div className="stm__combined">
				{/* ── Porta ──────────────────────────────────── */}
				<div
					className={`stm__goal ${readOnly ? "stm__goal--readonly" : ""} ${pendingOrigin ? "stm__goal--waiting" : ""}`}
					ref={goalRef}
					onClick={handleGoalClick}
				>
					<div className="stm__post stm__post--left" />
					<div className="stm__post stm__post--right" />
					<div className="stm__crossbar" />
					<div className="stm__net" />

					{normalizedPoints.map((point, index) => {
						const config = getOutcomeConfig(point);
						return (
							<div
								key={`goal-${index}`}
								className={`stm__point ${hoverPoint === index ? "stm__point--hover" : ""}`}
								style={{
									left: `${point.x}%`,
									top: `${point.y}%`,
									"--point-color": config.color,
								}}
								onClick={(e) => handleRemovePoint(index, e)}
								onMouseEnter={() => setHoverPoint(index)}
								onMouseLeave={() => setHoverPoint(null)}
							>
								<span className="stm__point-icon">{config.icon}</span>
							</div>
						);
					})}
				</div>

				{/* ── Meta campo ──────────────────────────────── */}
				<div
					className={`stm__field ${readOnly ? "stm__field--readonly" : ""}`}
					ref={fieldRef}
					onClick={handleFieldClick}
				>
					{/* Linee campo */}
					<div className="stm__field-markings">
						<div className="stm__field-area" />
						<div className="stm__field-small-area" />
						<div className="stm__field-penalty-dot" />
						<div className="stm__field-arc" />
						<div className="stm__field-midline" />
					</div>

					{/* SVG overlay per le linee di tiro */}
					<svg className="stm__field-svg">
						{renderFieldLines()}
					</svg>

					{/* Origini dei tiri */}
					{renderFieldOrigins()}

					{/* Pending origin */}
					{pendingOrigin && (
						<div
							className="stm__field-origin stm__field-origin--pending"
							style={{left: `${pendingOrigin.x}%`, top: `${pendingOrigin.y}%`}}
						>
							<span className="stm__field-origin-dot" />
							<span className="stm__field-origin-label">Ora clicca sulla porta</span>
						</div>
					)}
				</div>

				{/* Hint */}
				<div className="stm__hint">
					{readOnly ? "" : pendingOrigin
						? "Clicca sulla porta per segnare dove e arrivato il tiro"
						: "Clicca sul campo per segnare da dove parte il tiro (opzionale: puoi cliccare direttamente sulla porta)"
					}
				</div>
			</div>

			{/* ── Stats ───────────────────────────────────────── */}
			{normalizedPoints.length > 0 && (
				<div className="stm__stats">
					<div className="stm__stat">
						<span className="stm__stat-value">{stats.total}</span>
						<span className="stm__stat-label">Tiri</span>
					</div>
					<div className="stm__stat stm__stat--save">
						<span className="stm__stat-value">{stats.saves}</span>
						<span className="stm__stat-label">Parate</span>
					</div>
					<div className="stm__stat stm__stat--goal">
						<span className="stm__stat-value">{stats.goals}</span>
						<span className="stm__stat-label">Gol</span>
					</div>
					<div className="stm__stat stm__stat--shot">
						<span className="stm__stat-value">{stats.shots}</span>
						<span className="stm__stat-label">Fuori</span>
					</div>
					<div className="stm__stat stm__stat--rate">
						<span className="stm__stat-value">{stats.saveRate}%</span>
						<span className="stm__stat-label">% Parate</span>
					</div>
				</div>
			)}

			{/* ── Legend ───────────────────────────────────────── */}
			<div className="stm__legend">
				{OUTCOMES.map((o) => (
					<div key={o.value} className="stm__legend-item">
						<span className="stm__legend-dot" style={{background: o.color}}>{o.icon}</span>
						<span className="stm__legend-label">{o.label}</span>
					</div>
				))}
			</div>

			{/* ── Undo/Clear buttons ───────────────────────────── */}
			{!readOnly && normalizedPoints.length > 0 && (
				<div className="stm__actions">
					<button
						className="stm__action-btn"
						onClick={() => {
							onChange?.(normalizedPoints.slice(0, -1));
							setPendingOrigin(null);
						}}
					>
						↩ Rimuovi ultimo
					</button>
					<button
						className="stm__action-btn"
						onClick={() => {
							onChange?.([]);
							setPendingOrigin(null);
						}}
					>
						Pulisci tutto
					</button>
				</div>
			)}
		</div>
	);
};

export default ShotTargetMap;
